"use client"

import { createContext, useContext, useState, useCallback, type ReactNode } from "react"
import { type Locale, defaultLocale, locales } from "./config"

type Dictionary = Record<string, unknown>

interface I18nContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
  dictionary: Dictionary
}

const I18nContext = createContext<I18nContextType | null>(null)

interface I18nProviderProps {
  children: ReactNode
  initialLocale?: Locale
  initialDictionary: Dictionary
}

export function I18nProvider({ children, initialLocale = defaultLocale, initialDictionary }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  const [dictionary, setDictionary] = useState<Dictionary>(initialDictionary)

  const setLocale = useCallback(async (newLocale: Locale) => {
    if (!locales.includes(newLocale)) return

    const newDictionary = await import(`./locales/${newLocale}.json`).then((m) => m.default)
    setDictionary(newDictionary)
    setLocaleState(newLocale)

    // Store preference
    if (typeof window !== "undefined") {
      localStorage.setItem("zindra-locale", newLocale)
      document.documentElement.lang = newLocale
    }
  }, [])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split(".")
      let value: unknown = dictionary

      for (const k of keys) {
        if (value && typeof value === "object" && k in value) {
          value = (value as Record<string, unknown>)[k]
        } else {
          return key
        }
      }

      if (typeof value !== "string") return key

      if (params) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => String(params[paramKey] ?? `{{${paramKey}}}`))
      }

      return value
    },
    [dictionary],
  )

  return <I18nContext.Provider value={{ locale, setLocale, t, dictionary }}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider")
  }
  return context
}
